import { ArrowLeft, ArrowRight, Sparkles, Star } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "../components/ui/carousel";

import FellowsIntroSlide from "../components/slides/FellowsIntroSlide";
import FellowsPartnerPerksSlide from "../components/slides/FellowsPartnerPerksSlide";
import FellowsProgramFormatSlide from "../components/slides/FellowsProgramFormatSlide";
import FellowsThoughtLeaderContentSlide from "../components/slides/FellowsThoughtLeaderContentSlide";
import FellowsClinicianCreatorSlide from "../components/slides/FellowsClinicianCreatorSlide";
import FellowsDentistPartnerSlide from "../components/slides/FellowsDentistPartnerSlide";
import FellowsAdvisorySlide from "../components/slides/FellowsAdvisorySlide";

const fellowsSlides = [
  { id: 0, slug: "fellows-title", title: "AI Innovation Fellows" },
  { id: 1, slug: "fellows-intro", title: "The Program" },
  { id: 2, slug: "fellows-program-format", title: "Influence the Roadmap" },
  { id: 3, slug: "fellows-thought-leadership", title: "Thought Leadership" },
  { id: 4, slug: "fellows-clinician-creator", title: "Clinician Creator Suite" },
  { id: 5, slug: "fellows-dentist-partner", title: "Dentist Partner" },
  { id: 6, slug: "fellows-advisory", title: "Advisory Services" },
  { id: 7, slug: "fellows-partner-perks", title: "Partner Perks" },
  { id: 8, slug: "fellows-apply", title: "Apply Now" },
];

const AIFellowsPage = () => {
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [scale, setScale] = useState(1);
  const containerRef = React.useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const parentWidth = containerRef.current.offsetWidth;
        const targetWidth = 1400;
        const newScale = parentWidth / targetWidth;
        setScale(newScale);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!api) return;

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  useEffect(() => {
    if (!api) return;

    const handleKeyDown = (event) => {
      if (event.key === "ArrowRight") {
        api.scrollNext();
      } else if (event.key === "ArrowLeft") {
        api.scrollPrev();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [api]);

  return (
    <div className="min-h-screen xl:h-screen w-screen bg-neutral-900 flex flex-col items-center justify-start p-2 md:p-8 pt-4 xl:pt-4 overflow-y-auto xl:overflow-hidden overflow-x-hidden relative">
      {/* Slide counter */}
      <div className="w-full max-w-[1800px] flex justify-between items-center gap-3 mb-4 z-50">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-xl">
            <Star className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-white/60 font-medium">
              AI Innovation Fellows
            </span>
          </div>
        </div>
        <div className="text-sm text-white/40 font-medium">
          {current} / {count}
        </div>
      </div>

      <div className="w-full max-w-[1800px] flex flex-col xl:flex-row gap-6 items-center justify-center flex-1 min-h-0">
        {/* Table of Contents */}
        <div className="w-full xl:w-[260px] flex flex-col flex-1 xl:flex-none xl:h-full py-4 xl:pt-0 xl:pb-8 pr-0 xl:pr-2 order-2 xl:order-1 min-h-[300px]">
          <div className="pl-4 border-l border-white/10 space-y-2 py-2">
            {fellowsSlides.map((slide, index) => (
              <button
                key={slide.id}
                onClick={() => api?.scrollTo(index)}
                className={`group flex items-center gap-3 w-full text-left transition-all duration-300 relative py-1.5 ${
                  current === index + 1
                    ? "opacity-100"
                    : "opacity-40 hover:opacity-80"
                }`}
              >
                <div
                  className={`absolute -left-[17px] h-full w-[3px] rounded-full transition-all duration-500 ${
                    current === index + 1
                      ? "bg-blue-500 opacity-100 shadow-[0_0_10px_rgba(59,130,246,0.5)]"
                      : "bg-transparent opacity-0"
                  }`}
                />
                <span
                  className={`text-sm font-medium tracking-wide transition-all duration-300 truncate ${
                    current === index + 1
                      ? "text-blue-400 translate-x-1"
                      : "text-white"
                  }`}
                >
                  {slide.title}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Main Slide Container */}
        <div className="w-full xl:flex-1 max-w-[1400px] aspect-video relative order-1 xl:order-2 flex-shrink-0 group">
          <div
            ref={containerRef}
            className="w-full h-full overflow-hidden bg-transparent relative"
          >
            <div
              style={{
                width: "1400px",
                height: "787.5px",
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            >
              <Carousel setApi={setApi} className="w-full h-full">
                <div className="w-full h-full bg-white rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10 relative isolate transform-gpu">
                  <CarouselContent className="h-full ml-0">
                    {/* TITLE SLIDE */}
                    <CarouselItem className="h-full pl-0">
                      <div className="h-full w-full flex flex-row">
                        <div className="w-1/2 bg-slate-900 text-white flex flex-col justify-center p-20 relative overflow-hidden">
                          <div className="absolute top-0 left-0 w-64 h-64 bg-blue-500 rounded-full blur-3xl opacity-20 -translate-x-1/2 -translate-y-1/2" />
                          <div className="relative z-10">
                            <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-blue-500/10 text-blue-400 mb-6 border border-blue-500/20">
                              <Star className="w-10 h-10" />
                            </div>
                            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
                              AI Innovation <span className="text-blue-400">Fellows</span>
                            </h1>
                            <p className="text-xl md:text-2xl text-slate-400 max-w-2xl leading-relaxed">
                              For ambitious physicians who want to establish themselves as thought leaders for AI in dentistry.
                            </p>
                          </div>
                        </div>
                        <div className="w-1/2 bg-slate-100 relative h-full">
                          <img
                            src="/assets/dentist.jpg"
                            alt="Innovation Fellows"
                            className="w-full h-full object-cover object-center"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent flex items-end p-12">
                            <div className="text-white">
                              <p className="font-medium text-xl mb-2 flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-blue-400" />
                                Shaping the future of clinical AI
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CarouselItem>

                    <CarouselItem className="h-full pl-0"><FellowsIntroSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><FellowsProgramFormatSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><FellowsThoughtLeaderContentSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><FellowsClinicianCreatorSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><FellowsDentistPartnerSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><FellowsAdvisorySlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><FellowsPartnerPerksSlide /></CarouselItem>

                    {/* APPLY NOW */}
                    <CarouselItem className="h-full pl-0">
                      <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center relative overflow-hidden">
                        <div className="absolute top-[-200px] left-[-100px] w-[700px] h-[700px] rounded-full blur-[140px] opacity-25 pointer-events-none" style={{ background: "linear-gradient(135deg, #bfdbfe 0%, #c4b5fd 50%, #93c5fd 100%)" }} />
                        <div className="absolute bottom-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-25 pointer-events-none" style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }} />
                        <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />
                        <div className="relative z-10 flex flex-col items-center text-center px-8 max-w-2xl">
                          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-100 text-blue-600 mb-6 border border-blue-200 shadow-sm">
                            <Star className="w-8 h-8" />
                          </div>
                          <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight text-slate-900 mb-4">
                            Ready to <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">apply?</span>
                          </h1>
                          <p className="text-[16px] text-slate-500 max-w-xl mb-8 leading-relaxed font-medium">
                            Your application will be reviewed within 1-2 weeks. You may be invited to an interview with the leader of the program, the CMO of the MSU Health System. If given an acceptance, you'll have 7 days to accept your spot — we are only able to offer limited slots in each cohort.
                          </p>
                          <a
                            href="/ai-fellows-application"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            draggable={false}
                            className="inline-block px-10 py-4 bg-blue-600 hover:bg-blue-500 text-white text-lg font-bold rounded-2xl shadow-xl shadow-blue-500/25 transition-all duration-200 hover:shadow-blue-400/30 hover:scale-[1.02] cursor-pointer no-underline"
                          >
                            Apply Now
                          </a>
                        </div>
                      </div>
                    </CarouselItem>
                  </CarouselContent>
                </div>
              </Carousel>
            </div>
          </div>

          <Button
            variant="outline"
            size="icon"
            className="absolute -left-6 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 border-0 text-slate-800 hover:text-slate-900 h-12 w-12 rounded-full backdrop-blur-sm shadow-sm z-50 disabled:opacity-50"
            onClick={() => api?.scrollPrev()}
            disabled={!api || current <= 1}
          >
            <ArrowLeft className="h-6 w-6" />
            <span className="sr-only">Previous slide</span>
          </Button>

          <Button
            variant="outline"
            size="icon"
            className="absolute -right-6 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 border-0 text-slate-800 hover:text-slate-900 h-12 w-12 rounded-full backdrop-blur-sm shadow-sm z-50 disabled:opacity-50"
            onClick={() => api?.scrollNext()}
            disabled={!api || current >= count}
          >
            <ArrowRight className="h-6 w-6" />
            <span className="sr-only">Next slide</span>
          </Button>
        </div>
      </div>

      {/* Mobile Alert */}
      <div className="fixed bottom-0 left-0 right-0 bg-blue-600/90 backdrop-blur-md text-white text-center py-3 px-4 text-xs font-semibold xl:hidden z-50 border-t border-blue-400/30 shadow-lg">
        This slideshow is designed best to be viewed on a laptop.
      </div>
    </div>
  );
};

export default AIFellowsPage;
